import { useSyncExternalStore } from "react";

export type DeanExchangeStatus = "streaming" | "reconciling" | "complete" | "failed";

export interface DeanRuntimeExchange {
  requestId: number;
  exchangeId: string;
  conversationId: number;
  question: string;
  answer: string;
  progress?: string;
  startedAt: number;
  status: DeanExchangeStatus;
}

export interface DeanPendingExchange {
  exchangeId: string;
  conversationId: number;
  startedAt: number;
  status: DeanExchangeStatus;
}

export interface DeanConversationMessage {
  id?: number;
  exchangeId?: string | null;
  role: "user" | "assistant" | "system";
  content: string;
  createdAt: string | Date;
}

export interface DeanConversationLike {
  messages: DeanConversationMessage[];
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface StreamDeanMessageOptions {
  conversationId: number;
  exchangeId: string;
  content: string;
  fetchImpl?: typeof fetch;
  onAccepted?: () => void;
  onContent?: (answer: string) => void;
  onStatus?: (status: string) => void;
  signal?: AbortSignal;
}

interface BeginDeanExchangeOptions {
  exchangeId?: string;
  startedAt?: number;
  storage?: StorageLike | null;
}

const ACTIVE_CONVERSATION_KEY = "dean-active-conversation-id";
const PENDING_EXCHANGES_KEY = "dean-pending-exchanges-v1";
const SESSION_SCOPE_KEY = "dean-session-scope-v1";
let sessionScope: string | null = null;
let sessionRevision = 0;
const sessionListeners = new Set<() => void>();
const streamControllers = new Set<AbortController>();
function scopedKey(key: string) {
  return `${key}:${encodeURIComponent(sessionScope ?? "signed-out")}`;
}

export function getDeanSessionRevision() { return sessionRevision; }
export function hasDeanSession() { return sessionScope !== null; }
export function useDeanSessionRevision() {
  return useSyncExternalStore(
    (listener) => { sessionListeners.add(listener); return () => { sessionListeners.delete(listener); }; },
    getDeanSessionRevision,
    () => 0,
  );
}

export function setDeanSessionScope(scope: string | null, storage = getBrowserStorage(), force = false) {
  if (scope === sessionScope && !force) return false;
  const previousScope = sessionScope;
  try {
    const persistedScope = storage?.getItem(SESSION_SCOPE_KEY);
    for (const oldScope of new Set([previousScope, persistedScope])) {
      if (oldScope && (oldScope !== scope || force)) {
        storage?.removeItem(`${ACTIVE_CONVERSATION_KEY}:${encodeURIComponent(oldScope)}`);
        storage?.removeItem(`${PENDING_EXCHANGES_KEY}:${encodeURIComponent(oldScope)}`);
      }
    }
    // Never adopt legacy, account-independent state.
    storage?.removeItem(ACTIVE_CONVERSATION_KEY);
    storage?.removeItem(PENDING_EXCHANGES_KEY);
    if (scope) storage?.setItem(SESSION_SCOPE_KEY, scope);
    else storage?.removeItem(SESSION_SCOPE_KEY);
  } catch { /* Browser storage may be disabled. */ }
  sessionScope = scope;
  sessionRevision += 1;
  for (const controller of streamControllers) controller.abort();
  streamControllers.clear();
  runtimeExchanges.clear();
  notifyLifecycleChanged();
  for (const listener of sessionListeners) listener();
  return true;
}

export function isDeanNewChat(storage = getBrowserStorage()) {
  try { return storage?.getItem(scopedKey(ACTIVE_CONVERSATION_KEY)) === "new"; }
  catch { return false; }
}
export const DEAN_RECONCILE_INTERVAL_MS = 1_500;
// Candidate generation plus mandatory verification may require two bounded AI
// calls. Keep client recovery alive beyond that combined server-side window.
export const DEAN_RECONCILE_TIMEOUT_MS = 4 * 60 * 1000;

const runtimeExchanges = new Map<number, DeanRuntimeExchange>();
const listeners = new Set<() => void>();
let revision = 0;
let nextRequestId = 1;

function getBrowserStorage(): StorageLike | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function notifyLifecycleChanged() {
  revision += 1;
  for (const listener of listeners) listener();
}

function readPendingMap(storage: StorageLike | null): Record<string, DeanPendingExchange> {
  if (!storage) return {};
  try {
    const raw = storage.getItem(scopedKey(PENDING_EXCHANGES_KEY));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, DeanPendingExchange>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writePendingMap(
  pending: Record<string, DeanPendingExchange>,
  storage: StorageLike | null,
) {
  if (!storage) return;
  try {
    if (Object.keys(pending).length === 0) storage.removeItem(scopedKey(PENDING_EXCHANGES_KEY));
    else storage.setItem(scopedKey(PENDING_EXCHANGES_KEY), JSON.stringify(pending));
  } catch {
    // Storage is a recovery aid. The in-memory exchange remains authoritative
    // for the current page even when browser storage is unavailable.
  }
}

export function useDeanLifecycleRevision(): number {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    () => revision,
    () => 0,
  );
}

export function rememberDeanConversation(
  conversationId: number | null,
  storage: StorageLike | null = getBrowserStorage(),
) {
  if (!storage || !sessionScope) return;
  try {
    storage.setItem(scopedKey(ACTIVE_CONVERSATION_KEY), conversationId == null ? "new" : String(conversationId));
  } catch {
    // Best effort only; the current component still retains its active ID.
  }
}

export function readRememberedDeanConversation(
  storage: StorageLike | null = getBrowserStorage(),
): number | null {
  if (!storage || !sessionScope) return null;
  try {
    const value = Number(storage.getItem(scopedKey(ACTIVE_CONVERSATION_KEY)));
    return Number.isInteger(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

export function beginDeanExchange(
  conversationId: number,
  question: string,
  options: BeginDeanExchangeOptions = {},
): DeanRuntimeExchange {
  const {
    exchangeId = globalThis.crypto.randomUUID(),
    startedAt = Date.now(),
    storage = getBrowserStorage(),
  } = options;
  const exchange: DeanRuntimeExchange = {
    requestId: nextRequestId++,
    exchangeId,
    conversationId,
    question,
    answer: "",
    startedAt,
    status: "streaming",
  };
  runtimeExchanges.set(conversationId, exchange);

  const pending = readPendingMap(storage);
  pending[String(conversationId)] = {
    exchangeId,
    conversationId,
    startedAt,
    status: "streaming",
  };
  writePendingMap(pending, storage);
  notifyLifecycleChanged();
  return exchange;
}

export function getDeanRuntimeExchange(conversationId: number | null): DeanRuntimeExchange | null {
  if (conversationId == null) return null;
  return runtimeExchanges.get(conversationId) ?? null;
}

export function getDeanPendingExchange(
  conversationId: number | null,
  storage: StorageLike | null = getBrowserStorage(),
): DeanPendingExchange | null {
  if (conversationId == null) return null;
  return readPendingMap(storage)[String(conversationId)] ?? null;
}

export function updateDeanExchange(
  conversationId: number,
  requestId: number,
  patch: Partial<Pick<DeanRuntimeExchange, "answer" | "progress" | "status">>,
  storage: StorageLike | null = getBrowserStorage(),
): boolean {
  const current = runtimeExchanges.get(conversationId);
  if (!current || current.requestId !== requestId) return false;

  const next = { ...current, ...patch };
  runtimeExchanges.set(conversationId, next);

  if (patch.status) {
    const pending = readPendingMap(storage);
    const persisted = pending[String(conversationId)];
    if (persisted) {
      pending[String(conversationId)] = {
        ...persisted,
        ...(patch.status ? { status: patch.status } : {}),
      };
      writePendingMap(pending, storage);
    }
  }
  notifyLifecycleChanged();
  return true;
}

export function updateDeanPendingExchange(
  conversationId: number,
  status: DeanExchangeStatus,
  storage: StorageLike | null = getBrowserStorage(),
): boolean {
  const pending = readPendingMap(storage);
  const current = pending[String(conversationId)];
  if (!current) return false;
  pending[String(conversationId)] = { ...current, status };
  writePendingMap(pending, storage);
  notifyLifecycleChanged();
  return true;
}

export function clearDeanExchange(
  conversationId: number,
  requestId?: number,
  storage: StorageLike | null = getBrowserStorage(),
): boolean {
  const current = runtimeExchanges.get(conversationId);
  if (requestId != null && current && current.requestId !== requestId) return false;

  runtimeExchanges.delete(conversationId);
  const pending = readPendingMap(storage);
  delete pending[String(conversationId)];
  writePendingMap(pending, storage);
  notifyLifecycleChanged();
  return true;
}

export function conversationHasAnswerForExchange(
  conversation: DeanConversationLike | undefined,
  exchange: Pick<DeanPendingExchange, "exchangeId"> | null,
): boolean {
  if (!conversation || !exchange) return false;
  const userIndex = conversation.messages.findIndex(
    (message) => message.role === "user" && message.exchangeId === exchange.exchangeId,
  );
  return userIndex >= 0
    && conversation.messages
      .slice(userIndex + 1)
      .some(
        (message) => message.role === "assistant"
          && message.exchangeId === exchange.exchangeId,
      );
}

export function hasUnresolvedDeanExchange(
  conversationId: number,
  now = Date.now(),
  storage: StorageLike | null = getBrowserStorage(),
): boolean {
  const runtime = getDeanRuntimeExchange(conversationId);
  const pending = getDeanPendingExchange(conversationId, storage);
  const exchange = runtime ?? pending;
  return Boolean(
    exchange
      && exchange.status !== "failed"
      && now - exchange.startedAt < DEAN_RECONCILE_TIMEOUT_MS,
  );
}

export function conversationAwaitsAssistant(
  conversation: DeanConversationLike | undefined,
  now = Date.now(),
): boolean {
  if (!conversation) return false;
  const lastMessage = [...conversation.messages]
    .reverse()
    .find((message) => message.role === "user" || message.role === "assistant");
  if (!lastMessage || lastMessage.role !== "user") return false;
  const createdAt = new Date(lastMessage.createdAt).getTime();
  return Number.isFinite(createdAt) && now - createdAt < DEAN_RECONCILE_TIMEOUT_MS;
}

export function shouldPollDeanConversation(
  conversation: DeanConversationLike | undefined,
  pending: DeanPendingExchange | null,
  hasLiveStream: boolean,
  now = Date.now(),
): boolean {
  if (hasLiveStream) return false;
  if (pending) {
    if (pending.status === "failed") return false;
    if (conversationHasAnswerForExchange(conversation, pending)) return false;
    return now - pending.startedAt < DEAN_RECONCILE_TIMEOUT_MS;
  }
  return conversationAwaitsAssistant(conversation, now);
}

export class DeanStreamInterruptedError extends Error {
  constructor(message = "DEAN's response stream ended before completion") {
    super(message);
    this.name = "DeanStreamInterruptedError";
  }
}

export class DeanStreamResponseError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message);
    this.name = "DeanStreamResponseError";
  }
}

export async function streamDeanMessage({
  conversationId,
  exchangeId,
  content,
  fetchImpl = fetch,
  onAccepted,
  onContent,
  onStatus,
  signal,
}: StreamDeanMessageOptions): Promise<void> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  if (signal?.aborted) controller.abort();
  streamControllers.add(controller);
  try {
  const response = await fetchImpl(`/api/conversations/${conversationId}/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content, exchangeId }),
    credentials: "include",
    signal: controller.signal,
  });

  if (!response.ok) {
    let message = "Failed to send message";
    try {
      const body = await response.json() as { error?: string; message?: string };
      message = body.error || body.message || message;
    } catch {
      // Keep the stable fallback message when an intermediary returns HTML.
    }
    throw new DeanStreamResponseError(message, response.status);
  }

  const acceptedExchangeId = response.headers.get("X-Dean-Exchange-Id");
  if (acceptedExchangeId && acceptedExchangeId !== exchangeId) {
    throw new DeanStreamResponseError("DEAN accepted a different message exchange");
  }
  onAccepted?.();
  const reader = response.body?.getReader();
  if (!reader) throw new DeanStreamInterruptedError("DEAN returned no response stream");

  const decoder = new TextDecoder();
  let answer = "";
  let lineBuffer = "";
  let sawDone = false;
  let eventName = "";
  let dataLines: string[] = [];

  const processFrame = () => {
    if (dataLines.length === 0) {
      eventName = "";
      return;
    }
    let data: { content?: string; done?: boolean; error?: string; status?: unknown; message?: unknown; type?: string } | string;
    try {
      data = JSON.parse(dataLines.join("\n")) as typeof data;
    } catch {
      dataLines = [];
      eventName = "";
      return;
    }
    dataLines = [];
    const isStatusEvent = eventName === "status"
      || (typeof data === "object" && data !== null && data.type === "status");
    eventName = "";
    const statusValue = typeof data === "object" && data !== null
      ? (typeof data.status === "string" ? data.status : (isStatusEvent ? data.message : undefined))
      : (isStatusEvent && typeof data === "string" ? data : undefined);
    if (typeof statusValue === "string") {
      const status = statusValue.trim();
      if (status && status.length <= 200 && !/[\r\n]/.test(status)) onStatus?.(status);
      // Progress events are status-only. Never treat any accompanying data as
      // answer content, even if a malformed event includes a content property.
      return;
    }
    if (typeof data !== "object" || data === null || isStatusEvent) return;
    if (data.error) throw new DeanStreamResponseError(data.error);
    if (data.content) {
      answer += data.content;
      onContent?.(answer);
    }
    if (data.done) sawDone = true;
  };

  const processLine = (line: string) => {
    if (line === "") {
      processFrame();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).replace(/^ /, ""));
    } else if (line.startsWith("event:")) {
      eventName = line.slice(6).trim();
    }
  };

  while (!sawDone) {
    const { done, value } = await reader.read();
    if (done) {
      lineBuffer += decoder.decode();
      if (lineBuffer) {
        const lines = lineBuffer.split(/\r?\n/);
        for (const line of lines) processLine(line);
      }
      processFrame();
      break;
    }

    lineBuffer += decoder.decode(value, { stream: true });
    const lines = lineBuffer.split(/\r?\n/);
    lineBuffer = lines.pop() ?? "";
    for (const line of lines) {
      processLine(line);
      if (sawDone) break;
    }
  }

  if (!sawDone) throw new DeanStreamInterruptedError();
  } finally {
    streamControllers.delete(controller);
    signal?.removeEventListener("abort", abort);
  }
}